// Seed catalog for the public Motor Data pages (manufacturer claim vs Vecros
// bench measurement).
//
// The live source is the backend `/propulsion/combinations` API (see api.js).
// That API is auth-only today, so anonymous visitors — the whole point of this
// page — are served from here instead. Keep this seeded with a few real
// combinations so the page is never empty; everything else comes from the API.
//
// `claimed` rows are transcribed from the manufacturer's published test data,
// with `claimedSource` pointing at the page they came from. `measured` rows are
// OUR thrust-stand exports. Never put a number in `measured` that did not come
// off the bench — the whole page trades on that distinction.

import { ROUTES } from "../../../routes";

// Rows at or below this throttle are public. Above it, sign-in.
export const FREE_THROTTLE_MAX = 60;

// Combinations shown in full on the public index. The rest render locked.
export const FREE_COMBO_COUNT = 3;

// These pages mount twice: publicly at `/services/developer/motor-data/public`
// and, for signed-in users, at `/services/developer/motor-data` with app chrome.
// Links must stay inside whichever mount the visitor is already on, so paths are
// derived from the current location rather than hard-coded.
export const APP_BASE = ROUTES.motorData;
export const PUBLIC_BASE = `${APP_BASE}/public`;

export const basePath = (pathname = window.location.pathname) =>
  pathname.startsWith(PUBLIC_BASE) ? PUBLIC_BASE : APP_BASE;

export const comboPath = (slug, pathname) => `${basePath(pathname)}/${slug}`;

// Every motor gets a page, not just the ones we have bench data for: a card
// that does nothing on click is a dead end, and most of the catalog is
// spec-only. `motor/` keeps these off the combination namespace — combination
// slugs are server-assigned, motor slugs are derived here from brand + name.
export const motorSlug = (brand, name) =>
  `${brand} ${name}`.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const motorPath = (brand, name, pathname) =>
  `${basePath(pathname)}/motor/${motorSlug(brand, name)}`;

/**
 * One test row. Throttle is the join key between claimed and measured.
 * { throttle, voltage, current, rpm, thrust, power, efficiency, torque, temp }
 * thrust in gf, torque in N·m, temp in °C.
 */

const MN1330_CLAIMED = [
  { throttle: 40, voltage: 92.0, current: 14.47, rpm: 1253, thrust: 14117, power: 1331, efficiency: 10.61, torque: 8.36 },
  { throttle: 42, voltage: 92.0, current: 16.79, rpm: 1318, thrust: 15722, power: 1545, efficiency: 10.18, torque: 9.28 },
  { throttle: 44, voltage: 92.03, current: 19.32, rpm: 1383, thrust: 17409, power: 1778, efficiency: 9.79, torque: 10.26 },
  { throttle: 46, voltage: 92.03, current: 21.84, rpm: 1444, thrust: 19026, power: 2010, efficiency: 9.47, torque: 11.23 },
  { throttle: 48, voltage: 92.07, current: 24.86, rpm: 1508, thrust: 20768, power: 2289, efficiency: 9.07, torque: 12.28 },
  { throttle: 50, voltage: 92.03, current: 28.14, rpm: 1575, thrust: 22744, power: 2590, efficiency: 8.78, torque: 13.43 },
  { throttle: 52, voltage: 92.06, current: 31.77, rpm: 1638, thrust: 24669, power: 2925, efficiency: 8.44, torque: 14.61 },
  { throttle: 54, voltage: 92.05, current: 35.51, rpm: 1701, thrust: 26689, power: 3269, efficiency: 8.17, torque: 15.82 },
  { throttle: 56, voltage: 92.04, current: 39.4, rpm: 1760, thrust: 28741, power: 3626, efficiency: 7.93, torque: 17.0 },
  { throttle: 58, voltage: 92.03, current: 43.97, rpm: 1822, thrust: 30880, power: 4046, efficiency: 7.63, torque: 18.3 },
  { throttle: 60, voltage: 92.04, current: 49.17, rpm: 1886, thrust: 33219, power: 4526, efficiency: 7.34, torque: 19.71 },
  { throttle: 62, voltage: 92.03, current: 53.92, rpm: 1946, thrust: 35520, power: 4962, efficiency: 7.16, torque: 21.11 },
  { throttle: 64, voltage: 92.03, current: 59.27, rpm: 2005, thrust: 37880, power: 5455, efficiency: 6.94, torque: 22.55 },
  { throttle: 66, voltage: 92.02, current: 64.9, rpm: 2061, thrust: 40141, power: 5972, efficiency: 6.72, torque: 23.96 },
  { throttle: 68, voltage: 92.02, current: 71.24, rpm: 2118, thrust: 42579, power: 6555, efficiency: 6.5, torque: 25.49 },
  { throttle: 70, voltage: 92.01, current: 77.88, rpm: 2176, thrust: 45454, power: 7166, efficiency: 6.34, torque: 27.25 },
  { throttle: 80, voltage: 92.02, current: 112.91, rpm: 2434, thrust: 57770, power: 10390, efficiency: 5.56, torque: 34.98 },
  { throttle: 90, voltage: 91.99, current: 146.21, rpm: 2623, thrust: 66784, power: 13450, efficiency: 4.97, torque: 40.81 },
  { throttle: 100, voltage: 91.9, current: 177.03, rpm: 2744, thrust: 75415, power: 16270, efficiency: 4.64, torque: 46.25 },
];

export const SEED_COMBOS = [
  {
    slug: "tmotor-mn1330-kv40-ns52x20",
    motorBrand: "T-Motor",
    motorName: "MN1330 KV40",
    propName: "NS52x20",
    cells: "24S",
    specs: {
      kv: 40,
      "Max thrust (claimed)": "75 kg",
      "Peak current": "177 A",
      "Internal resistance": "19.5 mΩ",
      "Motor weight": "2750 g",
      Configuration: "36N42P",
      "IP rating": "IP45",
    },
    claimed: MN1330_CLAIMED,
    claimedSource: "http://uav-en.tmotor.com/2026/navigato_0403/1377.html",
    // Manufacturer states 117 °C surface temp after 10 min at 80% throttle,
    // 25 °C ambient.
    claimedTemp: { temp: 117, ambient: 25, throttle: 80, minutes: 10 },
    // No Vecros bench run published for this combination yet. The detail page
    // renders the claim alone and invites a test request — it does NOT invent
    // a comparison.
    measured: null,
    methodology: null,
  },
];

/** Percentage delta of measured vs claimed, or null when either is missing. */
export const delta = (measured, claimed) => {
  if (measured == null || claimed == null || claimed === 0) return null;
  return ((measured - claimed) / claimed) * 100;
};

/**
 * Join claimed and measured rows on throttle into one row per throttle point.
 * Rows present in either set survive; the missing side is null.
 * Either side may be null — an untested combination has `measured: null`, and a
 * default parameter would not catch that (defaults only fire on `undefined`).
 */
export const joinRows = (claimed, measured) => {
  const byThrottle = new Map();
  const put = (row, side) => {
    const key = row.throttle;
    if (!byThrottle.has(key)) byThrottle.set(key, { throttle: key, claimed: null, measured: null });
    byThrottle.get(key)[side] = row;
  };
  (claimed || []).forEach((r) => put(r, "claimed"));
  (measured || []).forEach((r) => put(r, "measured"));
  return [...byThrottle.values()].sort((a, b) => a.throttle - b.throttle);
};

export const comboTitle = (c) => `${c.motorBrand} ${c.motorName} + ${c.propName}`;

/* eslint-disable no-console */
// Cheapest place to catch a bad seed: this repo has no working test runner, and
// a claimed row missing `throttle` silently drops out of every join.
if (process.env.NODE_ENV !== "production") {
  const bad = SEED_COMBOS.flatMap((c) =>
    (c.claimed || []).concat(c.measured || []).filter((r) => typeof r.throttle !== "number")
  );
  if (bad.length) console.error(`motorData/catalog: ${bad.length} row(s) missing a numeric throttle`);

  // joinRows self-check — the one piece of non-trivial logic in this file.
  const j = joinRows([{ throttle: 50, thrust: 100 }], [{ throttle: 50, thrust: 90 }, { throttle: 60, thrust: 120 }]);
  console.assert(j.length === 2, "joinRows: expected 2 throttle points");
  console.assert(j[0].claimed?.thrust === 100 && j[0].measured?.thrust === 90, "joinRows: 50% row mismatched");
  console.assert(j[1].claimed === null, "joinRows: measured-only row should have null claimed");
  // An untested combination passes measured: null — must not throw.
  console.assert(joinRows([{ throttle: 40 }], null).length === 1, "joinRows: null measured should be tolerated");
  console.assert(joinRows(null, null).length === 0, "joinRows: both null should give no rows");
  console.assert(Math.round(delta(90, 100)) === -10, "delta: expected -10%");

  // basePath must not let a public visitor navigate into the app-chrome mount.
  console.assert(basePath(`${PUBLIC_BASE}/abc`) === PUBLIC_BASE, "basePath: public detail should stay public");
  console.assert(basePath(APP_BASE) === APP_BASE, "basePath: app index should stay in the app");
  console.assert(motorSlug("T-Motor", "MN1330 KV40") === "t-motor-mn1330-kv40", "motorSlug: unexpected slug");
  console.assert(motorSlug("SunnySky", "X4130 (KV380)") === "sunnysky-x4130-kv380", "motorSlug: parens/spaces");
}
